/** Ref-counts primary live-view owners so duplicate viewers do not stream twice. */

const claims = new Map<string, number>();
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) listener();
};

/**
 * Register a primary viewer for `url`. Returns the release function; callers
 * must invoke it on unmount or the URL stays claimed for the session.
 */
export function claimPrimaryLiveView(url: string): () => void {
  claims.set(url, (claims.get(url) ?? 0) + 1);
  notify();
  let released = false;
  return () => {
    // Guarded: React may invoke an effect's cleanup more than once under
    // StrictMode, and double-decrementing would free a claim still in use.
    if (released) return;
    released = true;
    const next = (claims.get(url) ?? 1) - 1;
    if (next > 0) claims.set(url, next);
    else claims.delete(url);
    notify();
  };
}

export function isPrimaryLiveViewClaimed(url: string): boolean {
  return (claims.get(url) ?? 0) > 0;
}

export function subscribeToLiveViewClaims(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam — the registry is module state and outlives a single render. */
export function resetLiveViewClaims(): void {
  claims.clear();
  notify();
}
